import { setUniversalFocus } from '../../../shared/util/accessibilityFocus';

import { useFocusEffect } from '@react-navigation/native';
import { type Component, type RefObject, useCallback, useRef } from 'react';

type FocusOptions = {
    /**
     * Whether to focus the target after the component renders.
     *
     * default: () => true
     */
    focusAfterRender?: () => boolean;

    /**
     * Delay in milliseconds before setting the focus.
     *
     * default: 100
     */
    delay?: number;

    onFocused?: () => void;
};

export default function useFocusOnNavigation(
    targetRef: RefObject<Component<unknown, unknown, unknown> | null>,
    options?: FocusOptions,
) {
    const delay = options?.delay ?? 100;

    // Stash the latest callbacks in refs so the focus effect identity stays
    // stable across renders. Without this, callers passing inline functions
    // would tear down and re-schedule the timeout on every parent render, and
    // an `onFocused` that triggers a re-render could loop indefinitely.
    const focusAfterRenderRef = useRef(options?.focusAfterRender);
    const onFocusedRef = useRef(options?.onFocused);
    focusAfterRenderRef.current = options?.focusAfterRender;
    onFocusedRef.current = options?.onFocused;

    const focusEffect = useCallback(() => {
        if (!(focusAfterRenderRef.current?.() ?? true)) {
            return;
        }

        const timeout = setTimeout(() => {
            setUniversalFocus(targetRef);
            onFocusedRef.current?.();
        }, delay);

        return () => clearTimeout(timeout);
    }, [delay, targetRef]);

    useFocusEffect(focusEffect);
}
